import $ from '../utils/net';
import Config from './config';
import User from './user';
import {h, Component} from 'preact';
import PFeed from './prematch-feed';
import LFeed from './live-feed';
import Observer from "../skins/softion-desktop/utils/observer";
import { MarketingEvents } from '../utils/gtm-module';
import { makeReactive } from '../utils/reactive-store';

export interface OddObj { id: string; name: string; value: number; selection?: string[]; closed?: boolean; live?:boolean }
export interface MarketObj { id: string; name: string; legatura: string; sub?: any; }
export interface EventObj { id: string; home: string; away: string; parent: string; short?: string; sub?: any; date?: number; }
export interface TournamentObj { id: string; name?: string; parent: string; sub?: any; }
export interface CategoryObj { id: string; name?: string; parent: string; sub?: any; }
export interface SportObj { id: string; name?: string; sub?: any; }

export interface CachedOdd {
    id: string;
    block: boolean;
    odd: OddObj;
    market: MarketObj;
    event: EventObj;
    tournament: TournamentObj;
    category: CategoryObj;
    sport: SportObj;
    changed: boolean;
}

export interface SystemCol {
    active?: boolean;
    bet: number | string;
    betTax: number | string;
    split: string | number;
    num: number;
}

export interface BonusDetail {
    import: { min: number };
    events: { min: number; max: number };
    odd: { min: number };
    percentage: number;
}

export interface BonusInfo {
    details: BonusDetail | any[];
    amount: string | number;
}

export interface TicketData {
    type: 'normal' | 'system';
    odds: Set<string>;
    cacheOdds: Record<string, CachedOdd>;
    bet: number | string;
    betTax: number | string;
    winBeforeTax: number | string;
    win: number | string;
    system: false | SystemCol[];
    bonus: BonusInfo | string | null;
    playas: number | string;
    autocharge: number;
    isBonusAdded?: boolean;
    bookedBetId?: string | null;
    saved?: number;
}

export interface DoneTicketData {
    type?: string;
    body?: string;
    code: string;
    idbets?: string | number;
    pendingId?: string | number;
    from?: number;
    invalidData?: any;
    reserved?: boolean;
    odds?: Record<string, {old: number, new: number}>;
    eventFaults?: string[];
    max?: string | number;
    teams?: string[];
    odd?: {match: string, segno: string};
    expires?: number
}

export type TicketStatus = 
{type: 'done'; data: DoneTicketData;} 
 | {type: 'accept-changes'; data: DoneTicketData[];} 
 | {type: 'error', data: {type: string, body:string, invalidData?: any}}
 | {type: 'pending'} 
 | {type: 'wait', data: {until: number}}
 | {type: 'preplacementwait', data: {from: any}}
 | {type: 'confirmflattening', data: {type, body}}
  | {type: 'confirmodds', data: {body: string, data: {odds: Record<string, {old: number, new: number}>}}}
  | {type: 'confirm-bonus-funds', data: {bonusAmount: number, needed: number}}

export interface UserPreferences {
    autoAcceptVariations?: boolean;
}

export interface ApiOddRow {
    id: string;
    evento: string;
    sportid: string;
    manifestazione: string;
    codice: string;
    data_evento: string;
    scommessa: string;
    legatura: string;
    esito: string;
    quota: string;
    selection?: string[];
}
// ----------------------------

const safe = <T, U>(cb: () => T, or: U): T | U => {try{return cb();}catch(e){return or;}}

/**
 * Questo commento serve per ricordarti come si gestiscono TUTTI gli errori possibili del ticket
 *
 *  const ERROR_TITLES = { . . . };
 *  const ERROR_MESSAGES = { . . . };
 *  const ERROR_INFO = {
 *  	"invalidData:invalid-teams-coupling": {
 *  		additionalData: (error) => <ul>{error.data.teams.map(e => <li>e</li>)}</ul>,
 *  	},
 *  	"invalidData:deleted-odd": {
 *  		additionalData: (error) => <span>{error.data.odd.match} - {error.data.odd.segno}</span>,
 *  	},
 *  	"invalidData:event-started": {
 *  		additionalData: (error) => <span>{!!error.data.eventFaults? error.data.eventFaults.join(' - ') : ''}</span>,
 *  	},
 *  	"invalidData:win-too-high": {
 *  		additionalData: (error) => <span>Max Win: {error.data.max}</span>,
 *  	}
 *  };
 *
 *  const NonEmptyTicketSate:
 *  	{Ticket.isNotPlaced() && Ticket.status.data.type == 'login' ? "Non sei loggato"}
 *  	{Ticket.isNotPlaced() ? ERROR_MESSAGES[Ticket.status.data.type+':'+Ticket.status.data.body] || (Ticket.status.data.type+':'+Ticket.status.data.body) : null}
 *  	{Ticket.isWaitingPrematch() ? "Please wait for the prematch bet" : null}
 *  	{Ticket.needsFlatteningConfirm() ? "La scommessa ora vince {Ticket.flattenwin}. Confermi?"+(() => Ticket.flattenAcceptFunction()) : null}
 *  	{Ticket.needsOddsChangeConfirm() ? {Object.values((Ticket.status || {data: {}}).data.data.odds).map((market) => ({market.match, market.name, market.old, market.new}))}; () => Ticket.flattenAcceptFunction()
 *  	{Ticket.needsUserApproval() && "Questo stato mi sa che non esite, ma indica che la schedina è giocata"}
 *  	{Ticket.isPlaced() && "Ok, hai giocato regolarmente"}
 *  	{Ticket.isPendingApproval() && "Il ticket è in accettazione"}
 *  	{Ticket.isWaitingLive() && "Apetta 8... 7... ecc..." && <TicketStatusWaitLive untilTime={Ticket.status.data.until} />}
 *
*/

export default class Ticket extends Component<any, any> {
    // 1. Creiamo dei "Box" reattivi una volta sola (stabili in memoria)
    private static _dataBox = makeReactive({ value: null });
    private static _statusBox = makeReactive({ value: false as TicketStatus | false });
    private static _userPrefsBox = makeReactive({ value: null as UserPreferences });
    private static _flattenwinBox = makeReactive({ value: null as number | string | null });
    private static _flattenAcceptBox = makeReactive({ value: null as (() => void) | null });

    // 2. Esponiamo Getter e Setter identici a prima.
    static get data() { return Ticket._dataBox.value; }
    static set data(val: TicketData) { Ticket._dataBox.value = val; }

    static get status() { return Ticket._statusBox.value; }
    static set status(val: TicketStatus | false) { Ticket._statusBox.value = val; }

    static get userPreferences() { return Ticket._userPrefsBox.value; }
    static set userPreferences(val: UserPreferences) { Ticket._userPrefsBox.value = val; }

    static get flattenwin() { return Ticket._flattenwinBox.value; }
    static set flattenwin(val: number | string | null) { Ticket._flattenwinBox.value = val; }

    static get flattenAcceptFunction() { return Ticket._flattenAcceptBox.value; }
    static set flattenAcceptFunction(val: (() => void) | null) { Ticket._flattenAcceptBox.value = val; }

    static conflictStrategy?: (odd: OddObj) => void;

    // Placeholders property
    static listenReset: (cb: () => void) => void;
    static saveempty: () => void;
    static reset: () => void;
    static reload: () => void;
    static continue: () => void;
    static reloadSettings: () => void;
    static save: () => void;
    static load: () => TicketData | false;
    static isSingle: () => boolean;
    static isSystem: () => boolean;
    static signal: () => void;
    static signalOddsClick: () => void;
    static getOdd: (path: string) => OddObj;
    static getMarket: (path: string) => MarketObj;
    static getEvent: (path: string) => EventObj;
    static getTournament: (path: string) => TournamentObj;
    static getCategory: (path: string) => CategoryObj;
    static getSport: (path: string) => SportObj;
    static hasOdds: () => boolean;
    static countOdds: () => number;
    static mapOdds: <T>(cb: (odds: CachedOdd & { newOdd: OddObj; changed: boolean; }) => T) => T[];
    static totalOdd: () => number;
    static eventNeeded: () => { min: number; max: number; minneeds: number; maxneeds: number; };
    static hasLiveOdds: () => string | undefined;
    static hasOddsOfEvent: (eid: string) => boolean;
    static canAddOdd: (odd: OddObj) => boolean;
    static getEventBetbuilder: (eid: string) => OddObj | null;
    static groupEvents: () => Record<string, number>;
    static hasDuplicateEvent: () => number | undefined;
    static canBeSystem: () => boolean;
    static canBeMultiple: () => boolean;
    static beSystem: () => boolean | undefined;
    static beMultiple: () => boolean | undefined;
    static respectsLegature: (numEvents: number) => boolean;
    static hasLegatureError: () => boolean;
    static getMissingLegatureNum: () => number;
    static canPlay: () => boolean;
    static canReserve: () => boolean;
    static hasAutoAcceptVariations: () => boolean;
    static toggleAutoAcceptVariations: () => boolean;
    static getTotImport: () => string;
    static setImport: (bet: number, split?: number | string) => void;
    static tmpToggleOddsBlock: (path: string) => void;
    static tmpToggleOdd: (path: string) => void;
    static tmpTogglePrematchOdd: (path: string) => void;
    static tmpHasPrematchOdd: (path: string) => boolean;
    static tmpToggleLiveOdd: (path: string) => void;
    static tmpHasLiveOdd: (path: string) => boolean;
    static markLiveOddsExpired: (odds: string[]) => void;
    static markPrematchOddsExpired: (odds: string[]) => void;
    static hasStatusMessage: () => boolean;
    static canResetStatus: () => boolean;
    static needsUserApproval: () => boolean;
    static isPlaced: () => boolean;
    static isNotPlaced: () => boolean;
    static isPendingApproval: () => boolean;
    static isWaitingLive: () => boolean;
    static isWaitingPrematch: () => boolean;
    static needsFlatteningConfirm: () => boolean;
    static needsOddsChangeConfirm: () => boolean;
    static waitingTimeRemaining: () => number;
    static subscribeSystemChanges: (cb: (data?: any) => void) => void;
    static unsubscribeSystemChanges: (cb: (data?: any) => void) => void;
    static subscribeOddsUpdates: (cb: () => void) => void;
    static unsubscribeOddsUpdates: (cb: () => void) => void;
    static systemMath: () => void;
    static play: () => void;
    static reserve: () => void;
    static bonusApply: () => void;
    static isBonusApplicable: () => BonusDetail | null | undefined;
    static fetchTicketsToConfirm: () => Promise<any[] | false>;
    static fetchLatestTicketsStatuses: () => Promise<{pending: string | boolean; accepted: string[]; refused: string[]}>;
    static fetchTicketStockStatus: (ticketCode: string) => Promise<{status: string; code: string}>;
    static pollServiceForStatusUpdate: () => Promise<void>;

    render() {
        return null;
    }

    static getMinBetAfterTax() {
        return parseFloat(Config.betting["min-bet-import"] || "0.00");
    }

    static getMinBet() {
        const minBetBase = parseFloat(Config.betting["min-bet-import"] || "0.00");
        return minBetBase * (1 + parseFloat(Config.betting["tax-bet-factor"] || "0.00"));
    }

    static getMinBonusBetAfterTax() {
        return parseFloat(Config.betting["min-bonus-bet-import"] || "0");
    }

    static getMinBonusBet() {
        const minBonusBase = parseFloat(Config.betting["min-bonus-bet-import"] || "0");
        return minBonusBase * (1 + parseFloat(Config.betting["tax-bet-factor"] || "0.00"));
    }

    static isBonusOnly(): boolean {
        const balance = User.balance();
        const bonus = User.bonus();
        return User.isLogged() && balance !== null && bonus !== null
            && balance > 0 && balance <= bonus + 0.01;
    }

    static willUseMixedAmount(bet: string | number): boolean {
        if (!User.isLogged()) return false;
        const real = User.balance() - User.bonus();
        const total = User.balance();
        if (real === null || total === null) return false;
        const betNum = parseFloat(bet as string);
        if (isNaN(betNum)) return false;
        return real > 0 && real < betNum && total >= betNum;
    }

    static getDefaultMinBet(): number {
        const minBonusBet = Ticket.getMinBonusBet();
        return Ticket.isBonusOnly() && minBonusBet > 0 ? minBonusBet : Ticket.getMinBet();
    }
}
const resetListeners: Array<() => void> =[];
Ticket.listenReset = (cb: () => void) => resetListeners.push(cb);
Ticket.saveempty = () => {
    const OldTicketData = Ticket.data;
    Ticket.data = {
        type: "normal",
        odds: new Set<string>(),//['/prematch/odd/654897/3/1', '/live/odd/547897/13456987/0']
        cacheOdds: {},
        bet: Ticket.getDefaultMinBet(),
		betTax: 0,
        winBeforeTax: 0,
        win: 0,
        system: false,
        bonus: null,
        playas: 0,
        autocharge: 0
    };
    Ticket.data.isBonusAdded = false;
    Ticket.bonusApply();
    Ticket.save();
    // Ticket.data = OldTicketData;
};
Ticket.reset = () => {
    Ticket.data = {
        type: "normal",
        odds: new Set<string>(),//['/prematch/odd/654897/3/1', '/live/odd/547897/13456987/0']
        cacheOdds: {},
        bet: Ticket.getDefaultMinBet(),
		betTax: 0,
        winBeforeTax: 0,
        win: 0,
        system: false,
        bonus: null,
        playas: 0,
        autocharge: 0,
        bookedBetId: null,
    };
    Ticket.status = false;
    Ticket.data.isBonusAdded = false;
    Ticket.bonusApply();
    Ticket.save()
    resetListeners.forEach(cb => cb());
}
Ticket.reload = () => {
    Ticket.data = Ticket.load() || {
        type: "normal",
        odds: new Set<string>(),//['/prematch/odd/654897/3/1', '/live/odd/547897/13456987/0']
        cacheOdds: {},
        bet: Ticket.getDefaultMinBet(),
		betTax: 0,
        winBeforeTax: 0,
        win: 0,
        system: false,
        bonus: null,
        playas: 0,
        autocharge: 0,
        bookedBetId: null,
    };

    Ticket.status = false;

    resetListeners.forEach(cb => cb());
};
Ticket.continue = () => {
    Ticket.status = false;
    resetListeners.forEach(cb => cb());
};
Ticket.reloadSettings = () => {
	Ticket.userPreferences = {
		autoAcceptVariations: Ticket.userPreferences.hasOwnProperty('autoAcceptVariations')
			? Ticket.userPreferences.autoAcceptVariations
			: Config.bettingDefaults.userAutoAcceptVariations,
	};
    const minBet = Ticket.getMinBet();
    const minBonusBet = Ticket.getMinBonusBet();
    const targetMin = Ticket.isBonusOnly() && minBonusBet > 0 ? minBonusBet : minBet;
    if (Ticket.data && !Ticket.data.bet) {
        Ticket.setImport(targetMin);
    }
    if (Number(Ticket.data.bet) + Number(Ticket.data.betTax) < targetMin) {
        Ticket.setImport(targetMin)
    }
};
Ticket.save = () => {
    const json = {...Ticket.data, odds: Array.from(Ticket.data.odds), saved: Date.now()};
    window.safeLocalStorage.setItem("ticket-data", JSON.stringify(json));
}
Ticket.load = (): TicketData | false => {
    return safe(() => {
        const item = window.safeLocalStorage.getItem("ticket-data");
        if (!item) return false;
        const json = JSON.parse(item) as TicketData;
        json.odds = new Set<string>(json.odds);
        if (json.saved && json.saved < Date.now() - 20*60e3) {
            return false; // Butta i ticket salvato più di 20 minuti fa
        }
        return json;
    }, false);
}
Ticket.status = false;
Ticket.userPreferences = {}; // Guarda in Ticket.reload()
Ticket.reload();
Ticket.isSingle = () => Ticket.data.odds.size <= 1;
Ticket.isSystem = () => Ticket.data.type == 'system';
Ticket.signal = () => {
    listeners.forEach(cb => cb());
}
Ticket.signalOddsClick = () => {
	oddsUpdatesListeners.forEach(cb => cb());
};

Ticket.getOdd = (path: string): OddObj => path.indexOf('/prematch') == 0
    ? PFeed.NStorage.get(path.replace('/prematch', '')) as OddObj
    : LFeed.getOdd(path.replace('/live', '')) as OddObj
Ticket.getMarket = (path: string): MarketObj => path.indexOf('/prematch') == 0
    ? PFeed.NStorage.get('/market/'+path.split('/').slice(3, 5).join('/')) as MarketObj
    : LFeed.getMarket('/market/'+path.split('/').slice(3, 5).join('/')) as MarketObj
Ticket.getEvent = (path: string): EventObj => path.indexOf('/prematch') == 0
    ? PFeed.NStorage.get('/event/'+path.split('/').slice(3, 4).join('/')) as EventObj
    : LFeed.getEvent('/event/'+path.split('/').slice(3, 4).join('/')) as EventObj
Ticket.getTournament = (path: string): TournamentObj => path.indexOf('/prematch') == 0
    ? PFeed.NStorage.get(Ticket.getEvent(path).parent) as TournamentObj
    : LFeed.getTournament(Ticket.getEvent(path).parent) as TournamentObj
Ticket.getCategory = (path: string): CategoryObj => path.indexOf('/prematch') == 0
    ? PFeed.NStorage.get(Ticket.getTournament(path).parent) as CategoryObj
    : Ticket.getTournament(path) as unknown as CategoryObj
Ticket.getSport = (path: string): SportObj => path.indexOf('/prematch') == 0
    ? PFeed.NStorage.get(Ticket.getCategory(path).parent) as SportObj
    : LFeed.menu[Ticket.getTournament(path).parent] as SportObj

Ticket.hasOdds = () => Ticket.data.odds.size > 0
Ticket.countOdds = () => Ticket.data.odds.size
Ticket.mapOdds = <T, J>(cb: (odds: CachedOdd & { newOdd: OddObj | null, changed: boolean }) => T): T[] => Array.from(Ticket.data.odds).map(path => {
    const newOdd = Ticket.getOdd(path);
    return cb({
        ...Ticket.data.cacheOdds[path],
        newOdd: newOdd,
        changed: !newOdd || Ticket.data.cacheOdds[path].odd.value != newOdd.value
    })
});
Ticket.totalOdd = () => Math.min(Array.from(Ticket.data.odds).reduce((tot: number, path: string) => tot*Ticket.data.cacheOdds[path].odd.value, 1), Config.betting['max-bet-multiplier']);

Ticket.eventNeeded = (): {min: number, max: number, minneeds: number, maxneeds: number} => {
    const legatureNonSoddisfatte = {min: 0, max: 0, minneeds: 0, maxneeds: 0};
    const oddsArray = Array.from(Ticket.data.odds);
    Ticket.mapOdds(odds => {
        const legatura = odds.market.legatura.split('-');
        //console.info(odds.odd.value, odds.market.legatura);

        if (parseInt(legatura[0]) > oddsArray.length) {
            legatureNonSoddisfatte.min = parseInt(legatura[0]);
            legatureNonSoddisfatte.minneeds = parseInt(legatura[0])-oddsArray.length;
        }

        if (parseInt(legatura[1]) < oddsArray.length) {
            legatureNonSoddisfatte.max = parseInt(legatura[1]);
            legatureNonSoddisfatte.maxneeds = oddsArray.length-parseInt(legatura[1]);
        }
    });
    //console.info(legatureNonSoddisfatte);
    return legatureNonSoddisfatte;
};

Ticket.hasLiveOdds = () => Array.from(Ticket.data.odds).find(id => id.indexOf('/live') == 0);
Ticket.hasOddsOfEvent = (eid: string) => !!Array.from(Ticket.data.odds).find(id => '/'+id.split('/').slice(2,4).join('/') == eid)
Ticket.canAddOdd = (odd: OddObj) => odd.value > 1 &&
    !((!Ticket.canBeSystem() || !!odd.live) && (Ticket.isSystem() || Ticket.hasOddsOfEvent(odd.id.split('/').slice(0,3).join('/'))))
// ! ((non può essere sistema o sarà live) && è/diventerà sistema)
Ticket.getEventBetbuilder = (eid: string): OddObj | null => {
	const eventOddsIds = Array.from(Ticket.data.odds).filter(id => id.split('/')[3] == eid.split('/')[2])
	const id = eventOddsIds.find(path => !!Ticket.data.cacheOdds[path].odd.selection);
	if (!id)
		return null;
	return Ticket.data.cacheOdds[id].odd;
}

Ticket.groupEvents = (): Record<string, number> => Array.from(Ticket.data.odds).map(path => path.split('/').slice(0,4).join('/')).reduce((c: Record<string, number>, p: string) => ({...c, [p]:(c[p]||0)+1}), {})
Ticket.hasDuplicateEvent = () => Object.values(Ticket.groupEvents()).find(v => v > 1);

Ticket.canBeSystem = () => !Ticket.hasLiveOdds();
Ticket.canBeMultiple = () => !Ticket.hasDuplicateEvent();
Ticket.beSystem = () => Ticket.canBeSystem() && (Ticket.data.type = 'system', Ticket.systemMath() as any);
Ticket.beMultiple = () => !Ticket.hasDuplicateEvent() && (Ticket.data.type = 'normal', Ticket.data.system = false);

Ticket.respectsLegature = (numEvents: number) =>
    !Array.from(Ticket.data.odds).map(path => Ticket.data.cacheOdds[path].market.legatura.split('-')).find(([min, max]) => parseInt(min) > numEvents || parseInt(max) < numEvents)

Ticket.hasLegatureError = () =>
    !Ticket.respectsLegature(Object.values(Ticket.groupEvents()).length);

Ticket.getMissingLegatureNum = () => {
    let numEvents = Object.values(Ticket.groupEvents()).length;
    const minMax = Array.from(Ticket.data.odds)
        .map(path => Ticket.data.cacheOdds[path].market.legatura.split('-'))
        .reduce((carry: number[], item: string[]) =>[Math.max(parseInt(item[0]),carry[0]),Math.min(parseInt(item[1]),carry[1])], [0,Infinity])
    const [min, max] = minMax;

    if (numEvents >= min && numEvents <= max) {
        return 0;
    }

    return min > numEvents? min - numEvents : max - numEvents;
};

// Il nome della funzione è "può piazzare". Non puoi piazzare se ci sono altri piazzamenti in corso
Ticket.canPlay = () =>
    !Ticket.hasStatusMessage() && (Ticket.respectsLegature(Object.values(Ticket.groupEvents()).length) || (Ticket.hasStatusMessage() && !Ticket.isPlaced() && !Ticket.isNotPlaced()))
Ticket.canReserve = () =>
    !Ticket.hasLiveOdds() && Ticket.canPlay()

Ticket.hasAutoAcceptVariations = () => {
	return !!Ticket.userPreferences.autoAcceptVariations;
}
Ticket.toggleAutoAcceptVariations = () => {
	return Ticket.userPreferences.autoAcceptVariations = !Ticket.userPreferences.autoAcceptVariations;
}

Ticket.getTotImport = () => (Number(Ticket.data.bet) + Number(Ticket.data.betTax)).toFixed(2);

function applyWinTax(win: number | string, bet: number | string): number {
	const w = parseFloat(win as string);
	const b = parseFloat(bet as string);
	return w < Config.betting['tax-win-from-amount'] ? w : (b + (w - b) * (1 - Config.betting['tax-win-factor']));
}

Ticket.setImport = (bet: number, split: number | string = 0) => {
	if (Ticket.status && Ticket.canResetStatus())
		Ticket.status = false;

	// C'è la tassa sul giocato
	const configBetTax = parseFloat(Config.betting['tax-bet-factor']);

    if (Ticket.data.type == 'normal') {
        /*
        console.log(Array.from(Ticket.data.odds).map(path => Ticket.data.cacheOdds[path].odd.value),
            Array.from(Ticket.data.odds).map(path => Ticket.data.cacheOdds[path].odd.value).reduce((a, b) => a * b, 1));
        */
        Ticket.data.bet = bet / (1 + configBetTax);
        Ticket.data.betTax = bet - (Ticket.data.bet as number);
		const maxOdd = Config.betting['max-bet-multiplier'] || Infinity;
		const totOdd = Array.from(Ticket.data.odds).map(path => Ticket.data.cacheOdds[path].odd.value).reduce((a: number, b: number) => a * b, 1)
		const rawWin = Math.min(totOdd, maxOdd) * (Ticket.data.bet as number);
        Ticket.data.win = Math.floor(rawWin * 100) / 100

        if (
            !!Config.betting['max-bet-win']
            && !!Config.betting['no-bet-limit']
            && Config.betting['no-bet-limit'] == 1
            && (Ticket.data.win as number) > parseFloat(Config.betting['max-bet-win'])
        ) {
            Ticket.data.win = parseFloat(Config.betting['max-bet-win']);
        }
		Ticket.data.winBeforeTax = Ticket.data.win;
		Ticket.data.win = applyWinTax(Ticket.data.win, Ticket.data.bet);
    } else {
        if (split != 0) {
            const colonna = (Ticket.data.system as SystemCol[]).find((S: SystemCol) => S.split == split);
            const betAfterTax = bet / (1 + configBetTax);
            colonna.bet = bet;
            colonna.betTax = bet - betAfterTax;
            Ticket.data.bet = (Ticket.data.system as SystemCol[]).reduce((c: number, S: SystemCol) => c+(Number(S.bet) - Number(S.betTax))*S.num, 0).toFixed(2);
            Ticket.data.betTax = (Ticket.data.system as SystemCol[]).reduce((c: number, S: SystemCol) => c+Number(S.betTax)*S.num, 0).toFixed(2);
        } else {
            const totCols = !!Ticket.data.system? Ticket.data.system.reduce((c: number, S: SystemCol) => c+((S.active||S.bet)?S.num:0), 0) : 1;
            Ticket.data.bet = bet / (1 + configBetTax);
			Ticket.data.betTax = bet - (Ticket.data.bet as number);
            if (!!Ticket.data.system) {
                Ticket.data.system.forEach((S: SystemCol) => {
					if (S.active || S.bet) {
						S.bet = (bet / totCols).toFixed(2)
						S.betTax = (Number(Ticket.data.betTax) / totCols).toFixed(2)
					}
				});
            }
        }
        Ticket.data.win = Ticket.data.winBeforeTax = 0;
    }

    Ticket.data.isBonusAdded = false;
    Ticket.bonusApply();
}

Ticket.tmpToggleOddsBlock = (path: string) => {
    if (!!Ticket.data.cacheOdds[path]) {
        Ticket.data.cacheOdds[path].block = !Ticket.data.cacheOdds[path].block;
        Ticket.systemMath();
    }
};

Ticket.tmpToggleOdd = (path: string) => {
    Ticket.data.bookedBetId = null;
    if (Ticket.status && Ticket.canResetStatus()) {
        Ticket.status = false;
    } else if (Ticket.status) {
        return; // Non puoi toccare le quote di un ticket live durante il countdown o mentre è in accettazione
    }
	Ticket.signalOddsClick();

    if (Ticket.data.odds.has(path)) {
        Ticket.data.odds.delete(path);
        delete Ticket.data.cacheOdds[path];

        if (Ticket.data.odds.size == 0)
            Ticket.reset();

        Ticket.data.system = false;

        if (Ticket.data.type == 'system')
            Ticket.systemMath()
        else
            Ticket.setImport(parseFloat(Ticket.getTotImport()));

        Ticket.save();
        return;
    }

    if (Ticket.canAddOdd(Ticket.getOdd(path))) {
        if (Ticket.hasOddsOfEvent('/'+path.split('/').slice(2,4).join('/')))
            Ticket.data.type = 'system';
        Ticket.data.cacheOdds[path] = {
            id: path,
            block: false,
            odd: Ticket.getOdd(path),
            market: {...Ticket.getMarket(path), sub: null},
            event: {...Ticket.getEvent(path), sub: null},
            tournament: {...Ticket.getTournament(path), sub: null},
            category: {...Ticket.getCategory(path), sub: null},
            sport: {...Ticket.getSport(path), sub: null},
            changed: false
        };
        Ticket.data.odds.add(path)
        Ticket.data.system = false;
        if (Ticket.data.type == 'system')
            Ticket.systemMath()
        else
            Ticket.setImport(parseFloat(Ticket.getTotImport()));
    } else if (Ticket.conflictStrategy) {
        Ticket.conflictStrategy(Ticket.getOdd(path));
    }

    Ticket.save();
}
Ticket.tmpTogglePrematchOdd = (path: string) => Ticket.tmpToggleOdd('/prematch'+path);
Ticket.tmpHasPrematchOdd = (path: string) => Ticket.data.odds.has('/prematch'+path);

Ticket.tmpToggleLiveOdd = (path: string) => Ticket.tmpToggleOdd('/live'+path);
Ticket.tmpHasLiveOdd = (path: string) => Ticket.data.odds.has('/live'+path);

Ticket.markLiveOddsExpired = (odds: string[]) => {
	for (let cacheKey in Ticket.data.cacheOdds) { // Preparati potrebbero inventarsi cazzate e questo serve
		delete Ticket.data.cacheOdds[cacheKey].odd.closed;
	}

	const closedIds = odds.map(odd => "/live"+odd)
	for (let cacheKey of closedIds) {
		if (!Ticket.data.cacheOdds[cacheKey]) { // Non capita, ma perché rischiare
			continue;
		}
		// Copio odd perché non devo modificare la quota nel feed, ma la versione sul ticket
		Ticket.data.cacheOdds[cacheKey].odd = {...Ticket.data.cacheOdds[cacheKey].odd};
		Ticket.data.cacheOdds[cacheKey].odd.closed = true;
	}
}
Ticket.markPrematchOddsExpired = (odds: string[]) => {
	for (let cacheKey in Ticket.data.cacheOdds) { // Preparati potrebbero inventarsi cazzate e questo serve
		delete Ticket.data.cacheOdds[cacheKey].odd.closed;
	}

	const closedIds = odds.map(odd => "/prematch"+odd)
	for (let cacheKey of closedIds) {
		if (!Ticket.data.cacheOdds[cacheKey]) { // Non capita, ma perché rischiare
			continue;
		}
		// Copio odd perché non devo modificare la quota nel feed, ma la versione sul ticket
		Ticket.data.cacheOdds[cacheKey].odd = {...Ticket.data.cacheOdds[cacheKey].odd};
		Ticket.data.cacheOdds[cacheKey].odd.closed = true;
	}
}

Ticket.hasStatusMessage = () => !!Ticket.status
Ticket.canResetStatus = () => (Ticket.isPlaced() || Ticket.isNotPlaced() || (Ticket.isPendingApproval() && !Config.bettingDefaults.waitPendingTickets));
Ticket.needsUserApproval = () => !!Ticket.status && Ticket.status.type=='accept-changes';
Ticket.isPlaced = () => !!Ticket.status && Ticket.status.type=='done';
Ticket.isNotPlaced = () => !!Ticket.status && Ticket.status.type=='error';
Ticket.isPendingApproval = () => !!Ticket.status && Ticket.status.type=='pending';
Ticket.isWaitingLive = () => !!Ticket.status && Ticket.status.type=='wait';
Ticket.isWaitingPrematch = () => !!Ticket.status && Ticket.status.type=='preplacementwait';
Ticket.isConfirmingBonusFunds = () => !!Ticket.status && Ticket.status.type=='confirm-bonus-funds';
Ticket.confirmBonusFunds = () => {
    Ticket.status = false;
    listeners.forEach(cb => cb());
    if (Ticket.data.type == 'normal') {
        if (!Ticket.hasLiveOdds())
            betPrematchTicket();
        else {
            mixedBet();
        }
    } else {
        betPrematchTicket();
    }
};
Ticket.needsFlatteningConfirm = () => !!Ticket.status && Ticket.status.type=='confirmflattening';
Ticket.needsOddsChangeConfirm = () => !!Ticket.status && Ticket.status.type=='confirmodds';
//@ts-ignore
Ticket.waitingTimeRemaining = () => !Ticket.status? 0 : Math.round(((Ticket.status.data.until || 0) - Date.now())/1000);

const listeners: Array<(data?: any) => void> =[];
Ticket.subscribeSystemChanges = (cb: (data?: any) => void) => {
    listeners.push(cb);
}
Ticket.unsubscribeSystemChanges = (cb: (data?: any) => void) => {
    listeners.splice(listeners.indexOf(cb), 1);
}

const oddsUpdatesListeners: Array<() => void> =[];
Ticket.subscribeOddsUpdates = (cb: () => void) => {
    oddsUpdatesListeners.push(cb);
}
Ticket.unsubscribeOddsUpdates = (cb: () => void) => {
    oddsUpdatesListeners.splice(oddsUpdatesListeners.indexOf(cb), 1);
}

const eventToCode = (path: string): string => [Ticket.data.cacheOdds[path].tournament.id.split('/')[3], ...path.split('/').slice(3)].join('-')

Ticket.systemMath = () => {
    $.post(Config.softionAPI + '/schedina/analizzaScommessa?json', {
        body: {
            schedina: Array.from(Ticket.data.odds).map(path => ({
                id:[Ticket.data.cacheOdds[path].tournament.id.split('/')[4], ...path.split('/').slice(3)].join('-'),
                legatura: Ticket.data.cacheOdds[path].market.legatura,
                block: Ticket.data.cacheOdds[path].block ? 'true': 'false',
            })).reduce((c: Record<string, any>, o: any) => ({...c, [o.id]:o}), {})
        },
        json: true
    }, (err: any, data: any) => {
        Ticket.data.system = Object.entries(data.system||{}).map((S: any) => ({
            active: false,
            bet: 0,
			betTax: 0,
            split: S[0],
            num: S[1]
        }))
        if (Ticket.data.system) {
            listeners.forEach(cb => cb(data))
        }
        Ticket.save();
    })
}
/**
 * Come funziona play:
 * Una volta che avvii una procedura di bet, cambia Ticket.status fino a che non termina la procedura.
 * I vari stati sono visibili sotto Ticket.needsUserApproval
 */
Ticket.play = () => {
    if (!Ticket.canPlay())
        return;
    if (Ticket.willUseMixedAmount(Ticket.data.bet)) {
        const needed = parseFloat(Ticket.data.bet as string) - (User.balance() - User.bonus());
        Ticket.status = {
            type: 'confirm-bonus-funds',
            data: {
                bonusAmount: User.bonus(),
                needed: Math.ceil(needed * 100) / 100,
            }
        };
        listeners.forEach(cb => cb());
        return;
    }
    if (Ticket.data.type == 'normal') {
        if (!Ticket.hasLiveOdds())
            betPrematchTicket();
        else {
            mixedBet();
        }
    } else {
        betPrematchTicket();
    }
}
Ticket.reserve = () => {
    if (!Ticket.canPlay())
        return;
    if (Ticket.data.type == 'normal') {
        if (!Ticket.hasLiveOdds())
            betPrematchTicket(true);
    } else {
        betPrematchTicket(true);
    }
}

Ticket.bonusApply = () => {
    const bonusDetail = Ticket.isBonusApplicable();

	if (bonusDetail) { // Se il bonus non è applicabile sta bene nullo
	    if (!Ticket.data.isBonusAdded) { // Se il bonus è applicabile ma già applicato non farlo due volte
			const totOdd = Array.from(Ticket.data.odds)
				.map(path => Config.betting['live-bonus-allow']!==1 && path.indexOf('/live')>-1? null : Ticket.data.cacheOdds[path])
				.reduce((c: number, R: CachedOdd | null) => R!==null? c*R.odd.value : c, 1);

			const totWin = Number(Ticket.data.bet) * Math.min(totOdd, Config.betting['max-bet-multiplier'] || Infinity);
			const winIsTooHigh = (!!Config.betting['max-bet-win'] && Config.betting['no-bet-limit'] == 1 && totWin > parseFloat(Config.betting['max-bet-win']));
			const realWin = winIsTooHigh ? parseFloat(Config.betting['max-bet-win']) : totWin;
			const bonusAmount = bonusDetail.percentage * realWin

	        Ticket.data.bonus = {
	            details: bonusDetail,
	            amount: bonusAmount.toFixed(2)
	        };
	        Ticket.data.win = parseFloat(realWin.toString()) + parseFloat((Ticket.data.bonus as BonusInfo).amount as string);
	        Ticket.data.isBonusAdded = true;

            if (
                !!Config.betting['max-bet-win']
                && !!Config.betting['no-bet-limit']
                && Config.betting['no-bet-limit'] == 1
                && (Ticket.data.win as number) > parseFloat(Config.betting['max-bet-win'])
            ) {
                Ticket.data.win = parseFloat(Config.betting['max-bet-win']);
				Ticket.data.bonus = Math.max(0, (Ticket.data.win as number) - realWin).toFixed(2);
            }
			Ticket.data.winBeforeTax = Ticket.data.win;
			Ticket.data.win = applyWinTax(Ticket.data.win, Ticket.data.bet);
	    }
	} else {
        Ticket.data.bonus = {
			details:[],
			amount: 0
		};
		Ticket.data.isBonusAdded = true;
    }
};

Ticket.isBonusApplicable = (): BonusDetail | null | undefined => {
    let BONUSLIST = PFeed.bonus as BonusDetail[];
    const inRange = ([max, min]: number[], n: number) => n <= max && n >= min;

    if (!BONUSLIST || !BONUSLIST.length) {
        return null;
    }

    return BONUSLIST.find((B: BonusDetail) => {
        return Number(Ticket.data.bet) > B.import.min
            && inRange(
                [B.events.min, B.events.max],
                Array.from(Ticket.data.odds)
                    .map(path => Config.betting['live-bonus-allow']!==1 && path.indexOf('/live')>-1? null : Ticket.data.cacheOdds[path])
                    .filter((O: CachedOdd | null) => O!==null && O.odd.value >= B.odd.min).length
            )
    });
};

const betPrematchTicket = (reserve: boolean = false) => {
    const isSystem = Ticket.data.type == 'system';
    const system = !isSystem ? {} :
        (Ticket.data.system as SystemCol[]).reduce((c: {systemimport: Record<string, number>, systemchoose: Record<string, string>}, T: SystemCol) => T.bet == 0||!T.bet?c:(c.systemimport[T.split]=parseFloat(T.bet as string),c.systemchoose[T.split]='on', c), {systemimport:{}, systemchoose:{}});

	// #> Tolgo il bonus perché c'è un problema con le API piazzaScommessa e non ritornano il bonus sui ticket
    const OldWin = Ticket.data.win;

    Ticket.status = {
        type: 'preplacementwait',
        data: {
            from: Date.now()
        }
    };

    $.post(Config.softionAPI + '/schedina/piazzaScommessa?json&l=' + Config.lang, {
        body: {
            betdata: JSON.stringify(Array.from(Ticket.data.odds).map(path => Ticket.data.cacheOdds[path]).reduce((c: Record<string, any>, {id, event, market, odd, tournament, category, block}: CachedOdd) =>
                Object.assign(c, {[eventToCode(id)]: {
                        evento: event.home+' - '+event.away,
                        segno: odd.name,
                        quota: odd.value.toFixed(2),
                        scommessa: market.name,
                        legatura: market.legatura,
                        esito: odd.id.split('/').slice(-1)[0],
                        codice: event.short,
                        manifestazione: tournament.id.split('/').slice(-1)[0],
                        block: block && Ticket.isSystem(),
						selection: odd.selection && odd.selection.map((oId: string) => oId.split('/').slice(-2).join('-'))
                    }}), {})
            ),
            betimport: Ticket.getTotImport(),
            betodd: Array.from(Ticket.data.odds).map(path => Ticket.data.cacheOdds[path]).map((R: CachedOdd) => R.odd.value).reduce((a: number, b: number)=>a*b, 1),
            autocharge: !!Ticket.data.autocharge? 1 : 0,
            playas: Ticket.data.playas==0? '' : Ticket.data.playas,
            system: encodeURIComponent(isSystem ? $.serialize(system) : ''),
            reservation: reserve ? 1 : 0
        }, json: true
    }, (err: any, body: any) => {
        if (!body.pendingId) {
            Ticket.status = {
                type: "error",
                data: {
					...body.dialog,
					type: body.dialog.type,
					body: (body.dialog.body || 'please-login').split(':')[0]
				}
            }
            Ticket.status.data.invalidData = body.invalidData;

            const expiredOddsIds = body.dialog.body != 'deleted-odd' && body.dialog.body != "event-started" ? [] : body.invalidData
                .map((code: string) => {const[_, event, market, outcome] = code.split('-'); return {event, market, outcome}})
                .map((odd: any) => `/odd/${odd.event}/${odd.market}/${odd.outcome}`);
            Ticket.markPrematchOddsExpired(expiredOddsIds)

            listeners.forEach(cb => cb());
            return console.log("Ticket non giocato: ", body);
        }

        const AcceptTicketFunction = () => {
            Ticket.flattenAcceptFunction = null;
            Ticket.flattenwin = null;
            const code = body.betId;
            const pendingId = body.pendingId;
            $.post(Config.softionAPI + '/schedina/accettaScommessa?json&l=' + Config.lang, {
                body: {
                    pendingId: body.pendingId,
                    reservation: reserve ? 'yes' : '',
                    playas: Ticket.data.playas==0? '' : Ticket.data.playas
                }, json: true
            }, (err: any, body: any) => {
                Ticket.status = {
                    type: body.dialog.body=='done'? (body.status == 'pending' ? 'pending' : 'done') : 'error',
                    data: {
                        code: code,
                        idbets: body.idbets,
                        pendingId: pendingId, // ID numerico
                        ...body.dialog,
						type: body.dialog.type,
						body: (body.dialog.body || '').split(':')[0], // Non lo so se esce, ma mi levo il pensiero
                        invalidData: body.invalidData,
                        reserved: reserve ? true : false
                    }
                };
                if ((Ticket.status as TicketStatus).type != 'error') {
                    if (!reserve && Ticket.data.bookedBetId) {
                        $.post(Config.softionAPI + '/schedina/saveBookedBetId', {
                            body: {
                                idbets: pendingId,
                                idprenotata: Ticket.data.bookedBetId
                            },
                            json: true
                        }, () => {});
                    }
                    window.dispatchEvent(new Event(MarketingEvents.BET_SUCCESS));
                }
                //Ticket.reset();
                listeners.forEach(cb => cb());
            });
        };

        const betWin = !!body.system && !!body.system.max_win_cut
            ? body.system.max_win_cut
            : body.betWin;

		// #> TODO Possiamo mettere un flag per le skin di Mariano così se cambiano le quote gli faccioa giocare lo stesso la schedina
        if (!!body.dialog && body.dialog.body === 'odds-changed') {
            Ticket.status = {
                type: "confirmodds",
                data: body.dialog // dialog: {body: "", data: {odds: {"1-2-123-3": {old: 1.01, new: 1.01}}}}
            };
            Ticket.flattenwin = betWin;
            Ticket.flattenAcceptFunction = AcceptTicketFunction;
			if (Ticket.hasAutoAcceptVariations()) {
				return Ticket.flattenAcceptFunction();
			}
            listeners.forEach(cb => cb());
            return console.log("Richiesta Conferma Ticket per variazione quota", body.dialog.data.odds);
        }

        //> E` intervenuto l'appiattimento
		// #> Le API ritornano la vincita al NETTO del bonus, quindi considero appiattita una scommessa che vince meno di bet*odd
        if (Math.round(parseFloat(betWin as string)) - Math.round(parseFloat(OldWin as string)) < -1) {
            Ticket.status = {
                type: "confirmflattening",
                data: {
					...body.dialog,
					type: body.dialog.type,
					body: body.dialog.body.split(':')[0]
				}
            };
            Ticket.flattenwin = betWin;
            Ticket.flattenAcceptFunction = AcceptTicketFunction;
            listeners.forEach(cb => cb());
            return console.log("Richiesta Conferma Ticket per appiattimento: ", betWin, ' < ', OldWin);
        }

        AcceptTicketFunction();
    });
};

const mixedBet = () => {
    Ticket.status = {
        type: 'wait',
        data: {
            until: null // Date.now()+10e3
        }
    };
    listeners.forEach(cb => cb());
    $.post(Config.softionAPI + '/newlive/placeLive?json', {
        body: {
            custom: {
                autocharge: !!Ticket.data.autocharge? 1 : 0,
                customerid: Ticket.data.playas==0? '' : Ticket.data.playas
            },
            'ticket-amount': Ticket.getTotImport(),
            'accept-var': Ticket.hasAutoAcceptVariations(),
            'ticket-body': Array.from(Ticket.data.odds).map(path => path.indexOf('/live')==0 ? {
                outcome: Ticket.data.cacheOdds[path].odd.id.split('/').slice(-1)[0],
                market: Ticket.data.cacheOdds[path].market.id.split('/').slice(-1)[0],
                event: Ticket.data.cacheOdds[path].event.id.split('/').slice(-1)[0],
                odd: Ticket.data.cacheOdds[path].odd.value
            } : {
                ignore: true,
                custom_id: eventToCode(path),
				selection: Ticket.data.cacheOdds[path].odd.selection && Ticket.data.cacheOdds[path].odd.selection!.map((oId: string) => oId.split('/').slice(-2).join('-'))
            })
        }, json: true
    }, (err: any, data: any) => {
        if (err || data.error) {
            Ticket.status = {
                type: 'error',
                data: {
                    type: data.error == 'please-login' ? 'login' : 'invalidData',
                    body: data.error
                }
            };
            listeners.forEach(cb => cb());
            return;
        }
        const liveWaitMilliseconds = parseInt(data['live-wait-seconds'] as string)*1000;

        Ticket.status = {
            type: 'wait',
            data: {
                until: Date.now()+liveWaitMilliseconds
            }
        };

        listeners.forEach(cb => cb());
        const timer = window.setInterval(() => {listeners.forEach(cb => cb())}, 1e3);

        setTimeout(() => {$.post(Config.softionAPI + '/newlive/confirmLive?json', {
            body: {
                token: data.token
            }, json: true
        }, (err: any, data: any) => {
            clearInterval(timer);

            if (err || data.error) {
                Ticket.status = {
                    type: "error",
                    data: {
                        type: data.error == 'please-login' ? 'login' : 'invalidData',
                        body: data.error
                    }
                };

				const expiredOddsIds = data.error != 'odds-closed' ?[] : (data.data ||[]).map((odd: any) => `/odd/${odd.event}/${odd.market}/${odd.outcome}`)
				Ticket.markLiveOddsExpired(expiredOddsIds)

                listeners.forEach(cb => cb());
                return;
            }
            // Ticket.status = false;
            Ticket.status = {
                type: data.success=='success'? 'done' : 'pending',
                data: {
                    ...data,
                    body: data.partner ? data.partner.msg : 'done'
                }
            };
            if ((Ticket.status as TicketStatus).type != 'error')
                window.dispatchEvent(new Event(MarketingEvents.BET_SUCCESS));
            listeners.forEach(cb => cb());
        })}, liveWaitMilliseconds);
    });
};


// STATI D'ERRORE

const ERROR_MESSAGES: Record<string, string> = {
	"invalidData:invalid-json": 				  'Formato de datos no válido (JSON)',
	"invalidData:service-disabled": 			  'No se puede utilizar este servicio',
	"invalidData:invalid-ticket": 				  '¡Ticket inválido!',
	"invalidData:invalid-data": 				  '¡Ticket inválido!',
	"invalidData:invalid-teams-coupling": 		  'No se pueden mezclar algunos de estos eventos',
	"invalidData:deleted-odd": 					  'Algunas cuotas ya no están disponibles',
	"invalidData:event-started": 				  'Uno o más eventos ya han empezado',
	"invalidData:invalid-bindings": 			  'Algunas cuotas no pueden jugarse juntas',
	"invalidData:invalid-specials-bindings": 	  'Algunas cuotas no pueden jugarse juntas',
	"invalidData:generic-error": 				  'Error interno',
	"invalidData:odds-changed": 				  'Algunas cuotas han cambiado',
	"invalidData:non-system-duplicate-event": 	  'Un evento está presente varias veces',
	"invalidData:invalid-system-imports": 		  'Una o más importaciones del sistema no son válidas',
	"invalidData:system-type-selected-no-import": 'No se puede apostar 0 en una columna del sistema',
	"invalidData:system-total-empty": 			  'No puede apostar 0.00',
	"invalidData:system-total-too-low": 		  'El importe de su apuesta es demasiado bajo',
	"invalidData:system-total-too-high": 		  'El importe de su apuesta es demasiado alto',
	"invalidData:system-single-too-high":		  'El importe de su apuesta es demasiado alto',
	"invalidData:win-too-high": 				  'Su importe de ganancia es demasiado alto',
	"invalidData:less_than_plaform":              'El importe de su apuesta es inferior al mínimo de la plataforma',
	"invalidData:more_than_plaform":              'El importe de su apuesta es superior al maximo de la plataforma',
	"invalidData:null_import": 				      'El importe de su apuesta es inferior al mínimo de la plataforma',
	"confirmBet:odds-changed": 					  'Algunas cuotas han cambiado.',
	"confirmBet:odd-not-compatible": 			  'Algunas probabilidades no pueden estar en el mismo billete. Por favor, inténtelo de nuevo',
	"message:account-blocked": 					  'Lo sentimos, no puede realizar apuestas porque su cuenta ha sido bloqueada',
	"message:internal-error": 					  '¡Error interno!',
	"message:betlimit":                           'Importación de la apuesta demasiado alta',
	"message:winlimit":                           'Importación de la ganancia demasiado alta',
	"message:maxload":                            'Rechazado por el operador',
	"message:match_maxload":                      'Rechazado por el operador',
	"message:repeatlimit":                        'Billete reimpreso demasiadas veces',
	"message:repeatwinlimit":                     'Billete reimpreso demasiadas veces',
	"message:bet-import-too-low":                 'Importación de apuestas demasiado baja',
	"message:bet-import-too-high":                'Importación de apuestas demasiado alta',
	"message:col-too-low":                        'Importación de apuestas por tipo de sistema demasiado baja',
	"message:win-too-high":                       'Importación de ganancias demasiado alta',
	"message:Not enough funds":                   'Por favor, recargue su cuenta',
	"message:below_bonus_min":                    'El mínimo para apuestas con bonos no se cumple',
	"message:bookmaker-set-limit":                'Tu apuesta supera el límite establecido por la casa de apuestas',
	"adminPrompt:affiliate-manual-recharge": 	  'Este cliente no tiene fondos'
};
const ERROR_TITLES: Record<string, string> = {
    default: "Atención",
    "confirmBet:odds-changed": "Attention!"
}
const ERROR_INFO: Record<string, { additionalData: (error: any) => preact.VNode }> = {
    "invalidData:invalid-teams-coupling": {
        additionalData: (error: any) => <ul>{error.data.teams.map((e: string) => <li>e</li>)}</ul>,
    },
    "invalidData:deleted-odd": {
        additionalData: (error: any) => <span>{error.data.odd.match} - {error.data.odd.segno}</span>,
    },
    "invalidData:event-started": {
        additionalData: (error: any) => <span>{!!error.data.eventFaults? error.data.eventFaults.join(' - ') : ''}</span>,
    },
    "invalidData:win-too-high": {
        additionalData: (error: any) => <span>Max Win: {error.data.max}</span>,
    }
}


/*
 * Ticket.pollServiceForStatusUpdate();
 * In automatico contatta il servizio e aggiorna lo stato del ticket da "pending" a
 * "done" oppure "error", in base alla decisione del dipartimento rischio.
 * Da chiamare ad intervalli regolari se il ticket è pending
 */

Ticket.fetchTicketsToConfirm = (): Promise<any[] | false> => {
	return new Promise((res, rej) => $.getJSON(Config.softionAPI + "/schedina/verificaRiserve?json", (something: any, data: any) => {
        if (!data.dialog)
			return res(false);
		const ticketsToConfirm = Object.values(data.dialog.body).map((B: any) => ({
			expires: Date.now() + 1000 * data.dialog.countdowns[B.idbets],
			live: B.tipo == "L",
			...B
		}));
		res(ticketsToConfirm);
	}));
}

Ticket.fetchLatestTicketsStatuses = (): Promise<{pending: string | boolean; accepted: string[]; refused: string[]}> => {
	return new Promise((res, rej) => $.getJSON(Config.softionAPI + "/schedina/ultimeScommesse?json", (something: any, data: any) => {
		// L'unica informazione necessaria per tutti i ticket è l'id.
		// Il resto lo hai tu in memoria se hai scaricato la lista ticket
		const ticketsData = {
			pending: !!data.checking && data.checking.last, // idmd5
			accepted: !!data.confirmed? data.confirmed.map((ticket: any) => ticket.last) :[],// lista di idmd5
			refused: !!data.deleted? data.deleted.map((ticket: any) => ticket.last) :[] // lista di idmd5
		}
        Observer.notify('ticketlogic-lastbets', data);
		res(ticketsData);
	}))
}

Ticket.fetchTicketStockStatus = async (ticketCode: string): Promise<{status: string; code: string}> => {
	const statuses = await Ticket.fetchLatestTicketsStatuses();
	const hasPendingTicket = !!statuses.pending;
	const isTicketAccepted = statuses.accepted.includes(ticketCode);
	const isTicketRefused = statuses.refused.includes(ticketCode);
	if (hasPendingTicket) {
		// Non usare await, va fatto in maniera asincrona.
		// È più un evento che una parte della procedura
		Ticket.fetchTicketsToConfirm().then(needsUserApproval => {
			if (!!needsUserApproval)
				return;
			console.log("Ora dovresti mostrare un popup per questi ticket: ", needsUserApproval);
		});
		// Fine dell'evento
		// Logica reale
		if (statuses.pending == ticketCode) {
			return {
				status: 'pending',
				code: ticketCode
			};
		}
	}
	return {
		status: isTicketAccepted ? 'done' : 'error',
		code: ticketCode
	}
}

Ticket.pollServiceForStatusUpdate = async (): Promise<void> => {
	// Lo stato attuale è:
	/* {
		type: body.dialog.body=='done'? (body.status == 'pending' ? 'pending' : 'done') : 'error',
		data: {
			code: code,
			idbets: body.idbets,
			pendingId: pendingId,
			...body.dialog,
			type: body.dialog.type,
			body: (body.dialog.body || '').split(':')[0], // Non lo so se esce, ma mi levo il pensiero
			invalidData: body.invalidData,
			reserved: reserve ? true : false
		}
	} */
	// Per sicurezza: c'è un ticket pending?
	const isWaitingPending = Ticket.isPendingApproval();
	if (!isWaitingPending)
		return;
    //@ts-ignore
	const ticketId = (Ticket.status as TicketStatus).data.code as string;
	const ticketStatus = await Ticket.fetchTicketStockStatus(ticketId);
	if (ticketStatus.status == 'pending')
		return;
	// lo status ora è done o error, non può essere pending
	(Ticket.status as TicketStatus).type = ticketStatus.status as "pending" | "done" | "error" | "accept-changes" | "wait" | "preplacementwait" | "confirmflattening" | "confirmodds";
	const status = Ticket.status as TicketStatus;
    if (ticketStatus.status == 'error' && status.type == 'error') {
		status.data.type = 'message';
		status.data.body = 'refused';
	}
	// Accettata o rifiutata non mi interessa, avvisa chi di dovere
	Ticket.signal();
}


export type RipetiScommessaResponse = {status: 'error'} | {status: 'ok', betImport: string, betDetail: Record<string, any>};

// =========================== RICHIAMA PRENOTATA =======================================
export class RichiamaPrenotataApi {
    static apiScommessaToNStorage(apiOddRow: ApiOddRow): EventObj {
        const [palinsesto, evento, scommessa, esito] = apiOddRow.id.split("-")
        return {
            id: '/event/'+evento,
            home: apiOddRow.evento.split(" - ")[0],
            away: apiOddRow.evento.split(" - ")[1],
            parent: '/tournament/'+[apiOddRow.sportid, palinsesto, apiOddRow.manifestazione].join('/'),
            sport: apiOddRow.sportid,
            short: apiOddRow.codice,
            date: new Date(apiOddRow.data_evento).getTime()/1000, // ts in secondi
            sub: {['/market/'+evento+'/'+scommessa]: {
                    id: '/market/'+evento+'/'+scommessa,
                    name: apiOddRow.scommessa,
                    legatura: apiOddRow.legatura,
                    sub: {['/odd/'+[evento, scommessa, esito].join('/')]: {
                            id: '/odd/'+[evento, scommessa, esito].join('/'),
                            name: apiOddRow.esito,
                            value: parseFloat(apiOddRow.quota),
							selection: apiOddRow.selection
                        }
                    }
                }
            }
        } as EventObj;
    }
    static tournamentOddToggler(path: string): () => void {
        return () => Ticket.tmpToggleOdd(path)
    }
    static toggleOddsFromQuickbet(outcome: any): void {
        //> Formatto l'outcome come se si trattasse del dettaglio di un ticket
        const details: Record<string, any> = {};
        details[outcome.code] = outcome;

        //> Cosi' uso le funzioni che ho copiaincollato da recallBet senza dover modificare nulla
        const mappedOdds = Object.entries(details)
            .map(([id, O]) => RichiamaPrenotataApi.apiScommessaToNStorage({id: id, ...O} as ApiOddRow))

        const eventsToStore = mappedOdds.reduce((c: Record<string, EventObj>, event: EventObj) => {c[event.id] = event; return c;}, {});
        PFeed.NStorage.storeData(eventsToStore);

        for (var oddId in details) {
            Ticket.tmpTogglePrematchOdd("/odd/"+oddId.split('-').slice(1).join('/'));
        }
    }
    
	/**
	 * C'è un problema con le API: mi danno una schedina che se contiene una betbuilder non è detto che si possa rigiocare.
	  * Cambio il codice esito per fare in modo di sistemarlo
	 */
	static preprocessBetDetail(betDetail: Record<string, any>): Record<string, any> {
		const fixedBetDetail = {...betDetail};
		for (var oddId in betDetail) {
			if (!fixedBetDetail[oddId].selection) {
				continue;
			}
			const newId = oddId.split('-').slice(0, -1).concat('0').join('-')
			fixedBetDetail[newId] = fixedBetDetail[oddId];
			delete fixedBetDetail[oddId];
		}
		return fixedBetDetail;
	}

    static recallBet(betId: string | number, done: (err: any, res?: any) => void): void { // test betId: 1226
        $.getJSON(Config.softionAPI + '/schedina/ripetiScommessa/'+betId+'/?json', (err: any, res: RipetiScommessaResponse) => {
            if (err) {
                return done(err, res);
            }
            if (!("betDetail" in res)) {
                return done("not-found", res);
            }
			RichiamaPrenotataApi.processPrenotata(res, betId);

            done(null, res);
        })
    }

    static loadBooked(betId: string | number, done: (err: any, res?: any) => void): void { // test betId: 1226
        $.post(Config.softionAPI + '/schedina/richiamaPrenotata/?json', {
            body: {code : betId},
            json: true
        }, (err: any, _res: {status: 'error', data: string} | {status:'success', data: {betamount: string, system: any, body: Record<string, any>}}) => {
            if (err) {
                return done(err, null);
            }

            const res: RipetiScommessaResponse = _res.status == 'error' ? {
                status: 'error'
            } : {
                status: 'ok', 
                betImport: _res.data.betamount, 
                betDetail: _res.data.body
            };

            if (!("betDetail" in res)) {
                return done("not-found", res);
            }
			RichiamaPrenotataApi.processPrenotata(res, betId);

            done(null, res);
        })
    }

    /**
     * Carica una scommessa già giocata e la ripropone nel carrello,
     * filtrando solo gli eventi ammissibili (prematch, senza risultato,
     * senza status, con data evento futura).
     *
     * @param betId - ID della schedina da rebettare
     * @param betDetailFromHistory - dettaglio eventi dalla bet history (con status/risultato)
     * @param betImport - importo originale della schedina
     * @param done - callback (err, res)
     */
    static rebetBet(
        betId: string | number,
        betDetailFromHistory: Record<string, any>,
        betImport: number,
        done: (err: any, res?: any) => void
    ): void {
        // Determina quali oddId sono ancora validi (prematch, senza risultato, futuri)
        const eligibleOddIds = new Set<string>();
        for (const [oddId, detail] of Object.entries(betDetailFromHistory)) {
            const d = detail as any;
            // Solo prematch: il dettaglio prematch non ha campo "event"
            // Evento valido: nessun risultato, nessuno status, data futura
            if (!d.event && !d.status && !d.risultato && new Date(d.data_evento) > new Date()) {
                eligibleOddIds.add(oddId);
            }
        }

        if (eligibleOddIds.size === 0) {
            return done("no-eligible-events");
        }

        $.getJSON(Config.softionAPI + '/schedina/ripetiScommessa/' + betId + '/?json', (err: any, res: RipetiScommessaResponse) => {
            if (err) {
                return done(err, res);
            }
            if (!("betDetail" in res)) {
                return done("not-found", res);
            }

            // Filtra il betDetail dell'API tenendo solo gli oddId eleggibili
            const filteredDetail: Record<string, any> = {};
            for (const [oddId, detail] of Object.entries(res.betDetail)) {
                if (eligibleOddIds.has(oddId)) {
                    filteredDetail[oddId] = detail;
                }
            }

            if (Object.keys(filteredDetail).length === 0) {
                return done("no-eligible-events", res);
            }

            const filteredRes = {
                ...res,
                betImport: betImport.toString(),
                betDetail: filteredDetail
            };
            RichiamaPrenotataApi.processPrenotata(filteredRes, betId);
            done(null, filteredRes);
        });
    }

    private static processPrenotata(res: { status: "ok"; betImport: string; betDetail: Record<string, any>; }, betId: string | number) {
        res.betDetail = this.preprocessBetDetail(res.betDetail);

        Ticket.reset();
        // Ticket.data.bookedBetId = betId; // Qui viene resettato da tmpTogglePrematchOdd
        Ticket.setImport(parseFloat(res.betImport));

        // res.data.system = {splitimport: {3: {amount: 0.215}}, fixed: ["eventCode-1"]}
        const mappedOdds = Object.entries(res.betDetail)
            .map(([id, O]) => RichiamaPrenotataApi.apiScommessaToNStorage({ id: id, ...O as any }));

        const eventsToStore = mappedOdds.reduce((c: Record<string, EventObj>, event: EventObj) => { c[event.id] = event; return c; }, {});
        PFeed.NStorage.storeData(eventsToStore);

        for (var oddId in res.betDetail) {
            try {
                Ticket.tmpTogglePrematchOdd("/odd/" + oddId.split('-').slice(1).join('/'));
            } catch (e) {
                console.error("Torneo non trovato");
                const event = PFeed.NStorage.get("/event/" + oddId.split('-')[1]) as EventObj;
                const tournament = (PFeed.NStorage.get(event.parent) as TournamentObj) || {
                    id: event.parent,
                    name: "",
                    parent: event.parent.replace('tournament', 'category').split('/').slice(-1).join('/')
                };
                const category = (PFeed.NStorage.get(tournament.parent) as CategoryObj) || {
                    id: tournament.parent,
                    name: "",
                    parent: tournament.parent.replace('category', 'sport').split('/').slice(-1).join('/')
                };
                const sport = (PFeed.NStorage.get(category.parent) as SportObj) || {
                    id: category.parent,
                    name: ""
                };

                PFeed.NStorage.storeData([
                    tournament,
                    category,
                    sport
                ]);
                try {
                    Ticket.tmpTogglePrematchOdd("/odd/" + oddId.split('-').slice(1).join('/'));
                } catch (e) {
                    console.error("Ci ho provato...");
                    console.error(e);
                }
            }
        }

        Ticket.data.bookedBetId = betId.toString();
    }
}
